// src/modules/feed/components/ActivitiesFeed/index.js
import React, { useState, useCallback, useContext } from 'react';
import { Card, Modal, Image, OverlayTrigger, Popover } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useAttachments } from './hooks/useAttachments';
import { formatDate, formatDuration, calculatePace, penaltyDaysLate } from '../../../../shared/utils/formatters';
import ActivityReactions from '../ActivityReactions';
import { AuthContext } from '../../../../contexts/AuthContext';
import './styles.css';

const getActivityIcon = (activityType) => {
    const iconMap = {
        'running': 'bi-person-walking',
        'cycling': 'bi-bicycle',
        'swimming': 'bi-water',
        'hiking': 'bi-tree',
        'walking': 'bi-person-walking',
        'football': 'bi-trophy',
        'badminton': 'bi-circle',
        'other': 'bi-activity'
    };
    return iconMap[activityType?.toLowerCase()] || 'bi-activity';
};

const getActivityIconColor = (activityType) => {
    const colorMap = {
        'running': 'text-danger',
        'cycling': 'text-primary',
        'swimming': 'text-info',
        'hiking': 'text-success',
        'walking': 'text-secondary',
        'football': 'text-warning',
        'badminton': 'text-primary',
        'other': 'text-muted'
    };
    return colorMap[activityType?.toLowerCase()] || 'text-muted';
};

const FeedActivityCard = ({ activity, onUsernameClick, onImageClick, resolvedAttachments, currentUsername }) => {
    const activityType = activity.activityType?.charAt(0).toUpperCase() + activity.activityType?.slice(1);
    const userName = activity.userGivenName || `${activity.firstName} ${activity.lastName}`;

    // Resolve attachment URLs
    const attachmentImages = activity.attachments?.map(att => ({
        ...att,
        url: resolvedAttachments[att.url] || att.url
    })) || [];

    const hasImages = activity.photos?.length > 0 || attachmentImages.length > 0;

    return (
        <div className="feed-activity-item">
            <div className="feed-activity-header">
                <div
                    className="feed-activity-avatar clickable"
                    onClick={(e) => {
                        e.preventDefault();
                        onUsernameClick?.(activity.username);
                    }}
                >
                    {activity.profilePicture ? (
                        <Image
                            src={activity.profilePicture}
                            roundedCircle
                            alt={userName}
                        />
                    ) : (
                        <div className="avatar-placeholder">
                            <i className="bi bi-person"></i>
                        </div>
                    )}
                </div>
                <div className="feed-activity-user-info">
                    <span
                        className="feed-activity-username clickable"
                        onClick={(e) => {
                            e.preventDefault();
                            onUsernameClick?.(activity.username);
                        }}
                    >
                        {userName}
                    </span>
                    <span className="feed-activity-type">
                        <i className={`bi ${getActivityIcon(activity.activityType)} ${getActivityIconColor(activity.activityType)} me-1`}></i>
                        {activityType}
                        {activity.source === 'strava' && (
                            <a
                                href={activity.stravaActivityId ? `https://www.strava.com/activities/${activity.stravaActivityId}` : undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="strava-badge-link ms-2"
                                onClick={(e) => e.stopPropagation()}
                                title="View on Strava"
                            >
                                <svg viewBox="0 0 24 24" width="14" height="14" fill="#fc4c02">
                                    <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
                                </svg>
                            </a>
                        )}
                    </span>
                </div>
                {activity.penaltyFactor !== undefined && activity.penaltyFactor !== null && activity.penaltyFactor < 1.0 && (
                    <OverlayTrigger
                        trigger={['hover', 'focus']}
                        placement="top"
                        overlay={
                            <Popover id={`penalty-popover-${activity.activityId}`}>
                                <Popover.Header>Late-logging Penalty</Popover.Header>
                                <Popover.Body>
                                    <p className="mb-1">Logged <strong>{penaltyDaysLate(activity.penaltyFactor)}</strong> after the activity date.</p>
                                    <p className="mb-1"><strong>{Math.round((1 - activity.penaltyFactor) * 100)}% reduction</strong> applied to points.</p>
                                    {activity.rawPoints !== undefined && (
                                        <p className="mb-1">{parseFloat(activity.rawPoints).toFixed(1)} raw pts × {activity.penaltyFactor} = <strong>{parseFloat(activity.points).toFixed(1)} pts</strong></p>
                                    )}
                                    <p className="mb-0 text-muted" style={{ fontSize: '0.8em' }}>20% reduction per day late (after 24 h), capped at 100%.{activity.username === currentUsername ? ' You can request an exemption on the activity page.' : ''}</p>
                                </Popover.Body>
                            </Popover>
                        }
                    >
                        <div className="feed-activity-penalty">
                            <span className="penalty-value">
                                <i className="bi bi-exclamation-triangle"></i>
                                {activity.penaltyFactor}×
                            </span>
                            <span className="penalty-label">{penaltyDaysLate(activity.penaltyFactor)}</span>
                        </div>
                    </OverlayTrigger>
                )}
                <div className="feed-activity-points">
                    <span className="points-value">{parseFloat(activity.points).toFixed(1)}</span>
                    <span className="points-label">points</span>
                </div>
            </div>

            <Link to={`/activity/${activity.activityId}`} className="feed-activity-content">
                <div className="feed-activity-stats-row">
                    <div className="feed-stat-item">
                        <i className="bi bi-geo-alt"></i>
                        <span>{parseFloat(activity.distance).toFixed(1)} km</span>
                    </div>
                    <div className="feed-stat-item">
                        <i className="bi bi-clock"></i>
                        <span>{formatDuration(activity.duration)}</span>
                    </div>
                    <div className="feed-stat-item">
                        <i className="bi bi-speedometer2"></i>
                        <span>{calculatePace(activity.duration, activity.distance, activity.activityType)}</span>
                    </div>
                    {activity.elevation > 0 && (
                        <div className="feed-stat-item">
                            <i className="bi bi-arrow-up-right"></i>
                            <span>{parseFloat(activity.elevation).toFixed(0)} m</span>
                        </div>
                    )}
                    {activity.calories > 0 && (
                        <div className="feed-stat-item">
                            <i className="bi bi-fire"></i>
                            <span>{activity.calories.toFixed(0)} kcal</span>
                        </div>
                    )}
                </div>

                {activity.description && (
                    <div className="feed-activity-description">
                        {activity.description}
                    </div>
                )}

                {hasImages && (
                    <div className="feed-activity-images">
                        {activity.photos?.map((photo, index) => (
                            <div
                                key={`photo-${index}`}
                                className="feed-activity-thumbnail"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onImageClick?.(photo);
                                }}
                            >
                                <Image src={photo} alt="Activity photo" />
                            </div>
                        ))}
                        {attachmentImages.map((attachment, index) => {
                            const fullUrl = activity.attachments[index].fullUrl || activity.attachments[index].url;
                            return (
                                <div
                                    key={`attachment-${index}`}
                                    className="feed-activity-thumbnail"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onImageClick?.(fullUrl);
                                    }}
                                >
                                    <Image src={attachment.url} alt={attachment.name} />
                                </div>
                            );
                        })}
                    </div>
                )}
            </Link>
            <ActivityReactions
                activityId={activity.activityId}
                initialReactions={activity.reactions || {}}
                initialReactionUsers={activity.reactionUsers || {}}
            />
        </div>
    );
};

const ActivitiesFeed = ({ activities, onUsernameClick }) => {
    const [enlargedImage, setEnlargedImage] = useState(null);
    const { user } = useContext(AuthContext);
    const currentUsername = user?.Username;
    const { attachments } = useAttachments(activities);

    const handleImageClick = useCallback((src) => {
        setEnlargedImage(src);
    }, []);

    const handleClose = useCallback(() => {
        setEnlargedImage(null);
    }, []);

    // Group activities by date
    const groupedActivities = React.useMemo(() => {
        return activities.reduce((acc, activity) => {
            const dateKey = formatDate(activity.date);
            if (!acc[dateKey]) {
                acc[dateKey] = [];
            }
            acc[dateKey].push(activity);
            return acc;
        }, {});
    }, [activities]);

    if (activities.length === 0) {
        return (
            <Card className="text-center py-5">
                <Card.Body>
                    <i className="bi bi-calendar-x d-block mb-3" style={{ fontSize: '2rem', opacity: 0.5 }}></i>
                    <p className="text-muted mb-0">No activities to display</p>
                </Card.Body>
            </Card>
        );
    }

    return (
        <div className="activities-feed">
            {Object.entries(groupedActivities).map(([date, dateActivities]) => (
                <Card key={date} className="feed-date-group">
                    <div className="feed-date-header">{date}</div>
                    {dateActivities.map((activity) => (
                        <FeedActivityCard
                            key={activity.activityId}
                            activity={{
                                ...activity,
                                userGivenName: activity.userGivenName || `${activity.firstName} ${activity.lastName}`
                            }}
                            onUsernameClick={onUsernameClick}
                            onImageClick={handleImageClick}
                            resolvedAttachments={attachments}
                            currentUsername={currentUsername}
                        />
                    ))}
                </Card>
            ))}
            <Modal
                show={!!enlargedImage}
                onHide={handleClose}
                centered
                size="lg"
            >
                <Modal.Body className="p-0 d-flex justify-content-center align-items-center">
                    {enlargedImage && (
                        <Image
                            src={enlargedImage}
                            fluid
                            style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain' }}
                        />
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

FeedActivityCard.propTypes = {
    activity: PropTypes.shape({
        activityId: PropTypes.string.isRequired,
        activityType: PropTypes.string.isRequired,
        points: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        distance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        duration: PropTypes.number.isRequired,
        username: PropTypes.string,
        userGivenName: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        profilePicture: PropTypes.string,
        description: PropTypes.string,
        photos: PropTypes.arrayOf(PropTypes.string),
        attachments: PropTypes.arrayOf(PropTypes.shape({
            url: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired
        }))
    }).isRequired,
    onUsernameClick: PropTypes.func,
    onImageClick: PropTypes.func,
    resolvedAttachments: PropTypes.objectOf(PropTypes.string)
};

ActivitiesFeed.propTypes = {
    activities: PropTypes.array.isRequired,
    onUsernameClick: PropTypes.func.isRequired
};

export default ActivitiesFeed;
